import { AUDIENCE, CLIENT_ID, DOMAIN } from '@/config/constants';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import {
    completeLogoutProcess,
    handleLoginResponse,
    trackLogin,
    trackLoginForMetrics,
} from 'state/actions/session.actions';
import { useEffect, useRef } from 'react';
import LoadingScreen from 'views/LoadingScreen';
import { addUser } from 'utils/api/user.api';
import { cachedFetchStore } from 'utils/app/localStorage';
import registerAuthenticatedFetchInterceptor from 'utils/api/registerAuthenticatedFetchInterceptor';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';

// TODO: remove this hook once local-dev user provisioning is automated.
function useSelfProvisionUserInDev(isAuthenticated) {
    // Idempotent bootstrap; react-query owns the retry and keeps failures in
    // mutation state instead of surfacing them as unhandled rejections.
    const { mutate: provisionUser } = useMutation(addUser, { retry: 3 });
    useEffect(() => {
        if (import.meta.env.DEV && isAuthenticated) {
            provisionUser();
        }
    }, [isAuthenticated, provisionUser]);
}

function SetupUser({ didRedirectCallback }) {
    const { user, isAuthenticated } = useAuth0();
    const dispatch = useDispatch();

    useSelfProvisionUserInDev(isAuthenticated);

    if (user) {
        dispatch(handleLoginResponse(user));
    }

    // ensure login is tracked ONCE after login and after identification
    if (user && didRedirectCallback.current === true) {
        dispatch(trackLogin());
        trackLoginForMetrics(user);
        window.__spate_pendingChurnZeroLogin = true;
        didRedirectCallback.current = false;
    }

    return null;
}

function SetupInterceptor() {
    const { getAccessTokenSilently, loginWithRedirect, isAuthenticated } =
        useAuth0();
    const interceptor = useRef();

    if (isAuthenticated) {
        if (interceptor.current) interceptor.current.unregister();
        const unregister = registerAuthenticatedFetchInterceptor({
            getAccessTokenSilently,
            loginWithRedirect,
        });
        interceptor.current = { unregister };
    }

    return null;
}

export function Auth0ProviderWithRedirectCallback({
    children,
    domain = DOMAIN,
    clientId = CLIENT_ID,
    redirectUri = `${location.protocol}//${location.host}/auth`,
    audience = AUDIENCE,
    scope = 'openid profile email',
    cacheLocation = 'localstorage',
    useRefreshTokens = true,
    ...props
}) {
    const history = useHistory();
    const didRedirectCallback = useRef();
    const onRedirectCallback = async (appState) => {
        didRedirectCallback.current = true;
        await cachedFetchStore.clear();
        history.replace((appState && appState.returnTo) || '/');
    };

    return (
        <Auth0Provider
            onRedirectCallback={onRedirectCallback}
            domain={domain}
            clientId={clientId}
            redirectUri={redirectUri}
            audience={audience}
            scope={scope}
            cacheLocation={cacheLocation}
            useRefreshTokens={useRefreshTokens}
            {...props}
        >
            <SetupInterceptor />
            <SetupUser didRedirectCallback={didRedirectCallback} />
            {children}
        </Auth0Provider>
    );
}

export function Logout() {
    const { logout } = useAuth0();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(completeLogoutProcess()).finally(() => {
            logout({
                returnTo: `https://www.spate.nyc`,
            });
        });
    }, [dispatch, logout]);

    return (
        <LoadingScreen>
            <h3 style={{ textAlign: 'center' }}>Logging out...</h3>
        </LoadingScreen>
    );
}
